import GeocodingRepository from '@src/repositories/GeocodingRepository';

class GeocodingService {
  constructor() {
    this.repository = new GeocodingRepository();
  }

  /**
   * Format place data to country option
   * @param {Object} place - Place data from API
   * @returns {Object} Formatted country option
   */
  static formatCountryOption(place) {
    const country = place.address?.country || place.display_name;
    return {
      value: country,
      label: country,
      countryCode: place.address?.country_code?.toUpperCase() || '',
      data: place,
    };
  }

  /**
   * Format place data to state/province option
   * @param {Object} place - Place data from API
   * @returns {Object} Formatted state option
   */
  static formatStateOption(place) {
    const state = place.address?.state || place.display_name;
    return {
      value: state,
      label: state,
      fullLabel: place.display_name,
      lat: parseFloat(place.lat),
      lon: parseFloat(place.lon),
      data: place.address,
    };
  }

  /**
   * Format place data to city option
   * @param {Object} place - Place data from API
   * @returns {Object} Formatted city option
   */
  static formatCityOption(place) {
    const city = place.address?.city || place.address?.town || place.address?.village || place.display_name;
    return {
      value: city,
      label: place.display_name,
      lat: parseFloat(place.lat),
      lon: parseFloat(place.lon),
      data: place.address,
    };
  }

  /**
   * Format place data to address option
   * @param {Object} place - Place data from API
   * @returns {Object} Formatted address option
   */
  static formatAddressOption(place) {
    return {
      value: place.display_name,
      label: place.display_name,
      lat: parseFloat(place.lat),
      lon: parseFloat(place.lon),
      address: {
        street: place.address?.road || '',
        streetNumber: place.address?.house_number || '',
        city: place.address?.city || place.address?.town || place.address?.village || '',
        state: place.address?.state || '',
        country: place.address?.country || '',
        countryCode: place.address?.country_code?.toUpperCase() || '',
        postcode: place.address?.postcode || '',
        suburb: place.address?.suburb || '',
        region: place.address?.region || place.address?.state || '',
      },
    };
  }

  /**
   * Fallback when reverse geocoding is unavailable (network / empty result).
   * @param {number} lat
   * @param {number} lon
   */
  static formatCoordinatesFallback(lat, lon) {
    const label = `${Number(lat).toFixed(6)}, ${Number(lon).toFixed(6)}`;
    return {
      value: label,
      label,
      lat: Number(lat),
      lon: Number(lon),
      address: {
        street: '',
        streetNumber: '',
        city: '',
        state: '',
        country: '',
        countryCode: '',
        postcode: '',
        suburb: '',
        region: '',
      },
      isCoordinatesFallback: true,
    };
  }

  /**
   * Extract full address string from place
   * @param {Object} place - Place data from API
   * @returns {string} Full address string
   */
  static getFullAddress(place) {
    const parts = [];
    const addr = place.address;

    if (addr?.road) parts.push(addr.road);
    if (addr?.house_number) parts.push(addr.house_number);
    if (addr?.suburb) parts.push(addr.suburb);
    if (addr?.city || addr?.town) parts.push(addr.city || addr.town);
    if (addr?.state) parts.push(addr.state);
    if (addr?.postcode) parts.push(addr.postcode);
    if (addr?.country) parts.push(addr.country);

    return parts.join(', ');
  }

  /**
   * Get list of countries matching query
   * @param {string} query - Search query
   * @returns {Promise<Array>} List of formatted country options
   */
  async getCountries(query) {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const results = await this.repository.searchCountries(query);
    
    if (!results || results.length === 0) {
      return [];
    }

    const countries = results
      .filter(place => place.address?.country)
      .map(GeocodingService.formatCountryOption);
    
    const uniqueCountries = Array.from(
      new Map(countries.map(item => [item.value, item])).values()
    );

    return uniqueCountries;
  }

  /**
   * Get list of states/provinces within a country
   * @param {string} country - Country name
   * @param {string} stateQuery - State search query
   * @returns {Promise<Array>} List of formatted state options
   */
  async getStates(country, stateQuery) {
    if (!country || !stateQuery || stateQuery.trim().length < 2) {
      return [];
    }

    const results = await this.repository.searchStates(country, stateQuery);
    
    if (!results || results.length === 0) {
      return [];
    }

    const states = results
      .filter(place => place.address?.state)
      .map(GeocodingService.formatStateOption);
    
    const uniqueStates = Array.from(
      new Map(states.map(item => [item.value, item])).values()
    );

    return uniqueStates;
  }

  /**
   * Get list of cities matching query within a country
   * @param {string} country - Country name
   * @param {string} cityQuery - City search query
   * @param {string} state - Optional state to filter by
   * @returns {Promise<Array>} List of formatted city options
   */
  async getCities(country, cityQuery, state = '') {
    if (!country || !cityQuery || cityQuery.trim().length < 2) {
      return [];
    }

    const results = await this.repository.searchCities(country, cityQuery);
    
    if (!results || results.length === 0) {
      return [];
    }

    let filteredResults = results;
    
    if (state && state.trim().length > 0) {
      filteredResults = results.filter(
        place => place.address?.state?.toLowerCase() === state.toLowerCase()
      );
    }

    return filteredResults
      .filter(place => place.address?.city || place.address?.town || place.address?.village)
      .map(GeocodingService.formatCityOption);
  }

  /**
   * Get list of streets matching query within a city
   * @param {string} country - Country name
   * @param {string} city - City name
   * @param {string} streetQuery - Street search query (can include house number)
   * @returns {Promise<Array>} List of formatted street/address options
   */
  async getStreets(country, city, streetQuery) {
    if (!country || !city || !streetQuery || streetQuery.trim().length < 2) {
      return [];
    }

    const results = await this.repository.searchAddress(country, city, streetQuery);
    
    if (!results || results.length === 0) {
      return [];
    }

    const streets = results.map(place => {
      // Check if it's a specific address (with house_number)
      const hasNumber = place.address?.house_number;
      
      // Get the street name
      const streetName = place.address?.road || place.display_name.split(',')[0].trim();
      
      // If it has a house number, include it in the value
      const fullAddress = hasNumber 
        ? `${streetName} ${place.address.house_number}` 
        : streetName;
      
      return {
        value: fullAddress,
        label: fullAddress,
        fullLabel: place.display_name,
        lat: parseFloat(place.lat),
        lon: parseFloat(place.lon),
        data: place.address,
        hasNumber: hasNumber,
        streetName: streetName,
        houseNumber: place.address?.house_number || '',
      };
    });

    const uniqueStreets = Array.from(
      new Map(streets.map(item => [item.value, item])).values()
    );

    return uniqueStreets;
  }

  /**
   * Get list of addresses matching query (with street number)
   * @param {string} country - Country name
   * @param {string} city - City name
   * @param {string} street - Street name
   * @param {string} addressQuery - Address query (usually street number)
   * @returns {Promise<Array>} List of formatted address options
   */
  async getAddresses(country, city, street, addressQuery = '') {
    if (!country || !city || !street) {
      return [];
    }

    const searchStreet = addressQuery ? `${addressQuery} ${street}` : street;
    const results = await this.repository.searchAddress(country, city, searchStreet);
    
    if (!results || results.length === 0) {
      return [];
    }

    return results.map(GeocodingService.formatAddressOption);
  }

  /**
   * Get coordinates from a full address string
   * @param {string} addressString - Full address string
   * @returns {Promise<Object|null>} Coordinates {lat, lon} or null
   */
  async getCoordinatesFromAddress(addressString) {
    if (!addressString || addressString.trim().length < 3) {
      return null;
    }

    const results = await this.repository.searchFreeText(addressString);
    
    if (!results || results.length === 0) {
      return null;
    }

    const firstResult = results[0];
    return {
      lat: parseFloat(firstResult.lat),
      lon: parseFloat(firstResult.lon),
      address: GeocodingService.formatAddressOption(firstResult).address,
    };
  }

  /**
   * Get address from coordinates
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {Promise<Object|null>} Address details or null
   */
  async getAddressFromCoordinates(lat, lon) {
    if (!lat || !lon) {
      return null;
    }

    const result = await this.repository.reverseGeocode(lat, lon);

    // Nominatim may return 200 + { error: "Unable to geocode" } with no display_name.
    if (!result || result.error || !result.display_name) {
      return null;
    }

    return GeocodingService.formatAddressOption(result);
  }

  /**
   * Search for any location with free text
   * @param {string} query - Search query
   * @returns {Promise<Array>} List of formatted location options
   */
  async searchLocations(query) {
    if (!query || query.trim().length < 3) {
      return [];
    }

    const results = await this.repository.searchFreeText(query);
    
    if (!results || results.length === 0) {
      return [];
    }

    return results.map(GeocodingService.formatAddressOption);
  }

  /**
   * Search using structured address components
   * @param {Object} addressParts - Address components
   * @returns {Promise<Array>} List of formatted address options
   */
  async searchStructured(addressParts) {
    const results = await this.repository.searchStructured(addressParts);
    
    if (!results || results.length === 0) {
      return [];
    }

    return results.map(GeocodingService.formatAddressOption);
  }

  /**
   * Validate if coordinates are valid
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {boolean} True if valid
   */
  static validateCoordinates(lat, lon) {
    return (
      typeof lat === 'number' &&
      typeof lon === 'number' &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180
    );
  }
}

export default GeocodingService;

